var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { customElement, property } from 'lit/decorators.js';
import { html } from 'lit';
import { BwElement } from './base/bw-element.js';
import { createKnob } from '@bandway/ui-primitives';
/**
 * <bw-knob> — rotary knob Web Component wrapping the createKnob primitive.
 *
 * Properties:
 * - value (number, reflect)  — current value
 * - min   (number)           — minimum, default 0
 * - max   (number)           — maximum, default 2
 * - resetValue (number)      — value on dblclick, default 1
 *
 * Events (mirror bw-volume-bar's input/change contract):
 * - bw-knob-input  { value: number }  — continuous, live during drag (engine)
 * - bw-knob-change { value: number }  — settle-point commit (persist)
 *   Both bubble + composed. Wire input→engine, change→persist; persisting on
 *   input causes a re-render storm (the pan-jitter bug).
 *
 * The primitive (createKnob from @bandway/ui-primitives) handles all
 * pointer / wheel / keyboard interactions. This component is a thin
 * wrapper providing the WC API surface.
 */
let BwKnob = class BwKnob extends BwElement {
    constructor() {
        super(...arguments);
        this.value = 1;
        this.min = 0;
        this.max = 2;
        this.resetValue = 1;
        // Slice 6 — production track-row pan knob uses these primitive options.
        this.centerDetent = 0;
        this.disabled = false;
        // CSS lives in packages/ui-components/src/styles/bw-knob.css —
        // consumers must import it (Storybook preview, web SPA, desktop renderer).
        // Lit `static styles` is a no-op in light DOM (no shadowRoot.adoptedStyleSheets).
        // See README "Light DOM + CSS prefix" section for full convention.
        this._knobHandle = null;
        this._lastInput = null;
    }
    render() {
        return html ``;
    }
    // Continuous, live value during a drag (rAF-coalesced by the primitive).
    // Consumers wire this to the engine so pan is audible WHILE dragging, but
    // must NOT persist here — persisting per-frame triggers a re-render storm.
    _emitInput(v) {
        if (this._lastInput === v)
            return;
        this._lastInput = v;
        this.value = v;
        this.dispatchEvent(new CustomEvent('bw-knob-input', {
            detail: { value: v },
            bubbles: true,
            composed: true,
        }));
    }
    // Settle-point commit (drag-end, wheel, key, dblclick) — the one-shot event
    // consumers wire to persistence. Mirrors bw-volume-bar's bw-input/bw-change
    // contract so pan and volume behave identically.
    _emit(v) {
        this.value = v;
        this.dispatchEvent(new CustomEvent('bw-knob-change', {
            detail: { value: v },
            bubbles: true,
            composed: true,
        }));
    }
    firstUpdated(_changed) {
        this._knobHandle = createKnob({
            initial: this.value,
            min: this.min,
            max: this.max,
            resetValue: this.resetValue,
            centerDetent: this.centerDetent,
            disabled: this.disabled,
            formatValue: this.formatValue,
            onChange: (v) => this._emitInput(v), // continuous → bw-knob-input
            onCommit: (v) => this._emit(v), // settle    → bw-knob-change
        });
        this.appendChild(this._knobHandle.el);
        // No manual dblclick/wheel/keydown sync needed: the primitive fires onCommit
        // synchronously for those settle points (which flushes the pending onChange
        // first), so both bw-knob-input and bw-knob-change are observable synchronously.
    }
    updated(changed) {
        // updated() runs on an async lit microtask that can fire after the element
        // disconnects (or, under test, with an incomplete knob stub). Guard that
        // the handle methods exist so a late update never throws an unhandled
        // rejection — same defensive posture as the optional-chained sync() above.
        if (changed.has('value') && typeof this._knobHandle?.getValue === 'function') {
            // Keep primitive value in sync when bound from outside; avoid feedback
            // loop because setValue does not re-invoke onChange.
            const current = this._knobHandle.getValue();
            if (current !== this.value) {
                this._knobHandle.setValue(this.value);
            }
        }
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this._knobHandle = null;
    }
};
__decorate([
    property({ type: Number, reflect: true })
], BwKnob.prototype, "value", void 0);
__decorate([
    property({ type: Number })
], BwKnob.prototype, "min", void 0);
__decorate([
    property({ type: Number })
], BwKnob.prototype, "max", void 0);
__decorate([
    property({ type: Number, attribute: 'reset-value' })
], BwKnob.prototype, "resetValue", void 0);
__decorate([
    property({ type: Number, attribute: 'center-detent' })
], BwKnob.prototype, "centerDetent", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], BwKnob.prototype, "disabled", void 0);
__decorate([
    property({ attribute: false })
], BwKnob.prototype, "formatValue", void 0);
BwKnob = __decorate([
    customElement('bw-knob')
], BwKnob);
export { BwKnob };
//# sourceMappingURL=bw-knob.js.map